// localeContext.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import i18n from "../i18n"; // your i18next init file

const LocaleContext = createContext({ lang: "en", setLang: () => {} });

export function LocaleProvider({ children }) {
  const [lang, setLangState] = useState(i18n.language || "en");

  // when i18next language changes (via detector, ?lng=fr, localStorage, etc.), sync context
  useEffect(() => {
    const handler = (lng) => setLangState(lng);
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  // when context changes language, tell i18next (so both stay in sync)
  const setLang = (lng) => i18n.changeLanguage(lng);

  // ensure <html lang="..."> updates
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const value = useMemo(() => ({ lang, setLang }), [lang]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => useContext(LocaleContext);

// Helper for objects like { en: "...", fr: "..." }
export function useL() {
  const { lang } = useLocale();
  return (value, fallback = "en") =>
    value && typeof value === "object" && !Array.isArray(value)
      ? value[lang] ?? value[fallback] ?? ""
      : value ?? "";
}
export function useI18nField() {
  const { lang } = useLocale();
  const fallback = "en";

  return (doc, key) => {
    if (!doc) return "";

    // 1) Direct map on the field: { en, fr }
    const direct = doc[key];
    if (direct && typeof direct === "object" && !Array.isArray(direct)) {
      // treat as i18n map if it has language keys
      if ("en" in direct || "fr" in direct) {
        return direct[lang] ?? direct[fallback] ?? "";
      }
    }

    // 2) i18n block created by your Cloud Function / extension
    const fromI18n =
      doc?.i18n?.[lang]?.[key] ?? doc?.i18n?.[fallback]?.[key];
    if (fromI18n) return fromI18n;

    // 3) Source block fallback (original content)
    const fromSource = doc?.i18nSource?.[key];
    if (fromSource) {
      if (typeof fromSource === "object" && !Array.isArray(fromSource)) {
        return fromSource[lang] ?? fromSource[fallback] ?? "";
      }
      return fromSource;
    }

    // 4) Suffixed fields: title_en/title_fr
    const suffixed = doc?.[`${key}_${lang}`] ?? doc?.[`${key}_${fallback}`];
    if (suffixed) return suffixed;

    // 5) Plain string fallback
    return typeof direct === "string" ? direct : "";
  };
}
